<template>
  <component
    v-if="phosphorComponent"
    :is="phosphorComponent"
    :size="pixelSize"
    :color="phosphorColor"
    :weight="weight"
    :mirrored="false"
    :aria-hidden="true"
    :class="classes"
  />
  <svg
    v-else
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    :class="classes"
    aria-hidden="true"
  >
    <component :is="path" />
  </svg>
</template>

<script setup>
import { computed, h } from 'vue';

// ── Phosphor Icons (barrel import — only what we use) ──
import {
  PhArrowRight,
  PhArrowSquareOut,
  PhArrowUp,
  PhArrowsClockwise,
  PhArticle,
  PhBell,
  PhBook,
  PhBookOpen,
  PhBookmark,
  PhBriefcase,
  PhCalendar,
  PhCamera,
  PhCaretDown,
  PhCaretLeft,
  PhCaretRight,
  PhChartBar,
  PhCheck,
  PhCheckCircle,
  PhClock,
  PhClockCounterClockwise,
  PhCode,
  PhCopy,
  PhCpu,
  PhCurrencyDollar,
  PhDatabase,
  PhDesktop,
  PhDeviceMobile,
  PhDevices,
  PhDownload,
  PhEnvelope,
  PhEye,
  PhEyeSlash,
  PhFacebookLogo,
  PhFileText,
  PhFlame,
  PhFolderSimple,
  PhGear,
  PhGitMerge,
  PhGlobe,
  PhGridFour,
  PhHammer,
  PhHourglass,
  PhPalette,
  PhPhone,
  PhHouse,
  PhImage,
  PhInfo,
  PhInstagramLogo,
  PhKey,
  PhLightbulb,
  PhLightning,
  PhLink,
  PhLinkedinLogo,
  PhList,
  PhLock,
  PhMagnifyingGlass,
  PhMapPin,
  PhMinusCircle,
  PhMonitor,
  PhPencil,
  PhPlay,
  PhPlus,
  PhPlusCircle,
  PhPrinter,
  PhPulse,
  PhQuestion,
  PhRobot,
  PhRocket,
  PhShare,
  PhShield,
  PhShieldCheck,
  PhShieldWarning,
  PhSignIn,
  PhSignOut,
  PhSliders,
  PhSmiley,
  PhSmileyMeh,
  PhSmileySad,
  PhSparkle,
  PhStack,
  PhStar,
  PhSword,
  PhTag,
  PhTarget,
  PhTextAlignLeft,
  PhTrash,
  PhTreeStructure,
  PhTrendDown,
  PhTrendUp,
  PhTrophy,
  PhTwitterLogo,
  PhUser,
  PhUsers,
  PhVideo,
  PhWarning,
  PhWarningCircle,
  PhWrench,
  PhX,
  PhXCircle,
  PhYoutubeLogo,
} from '@phosphor-icons/vue';

const props = defineProps({
  name:        { type: String, required: true },
  size:        { type: [Number, String], default: 18 },
  strokeWidth: { type: [Number, String], default: 1.75 },
  classes:     { type: String, default: '' },
  weight:      { type: String, default: 'duotone' },
  color:       { type: String, default: '' },
});

// ── Phosphor icon map — maps our canonical names to Phosphor components ──
const PHOSPHOR_ICONS = {
  // Semantic names used by report components
  pass: PhCheckCircle,
  fail: PhXCircle,
  warn: PhWarning,
  tip: PhLightbulb,
  target: PhTarget,
  trash: PhTrash,
  fast: PhLightning,
  shield: PhShieldCheck,
  content: PhArticle,
  link: PhLink,
  globe: PhGlobe,
  chart: PhChartBar,
  trophy: PhTrophy,
  device: PhDevices,
  search: PhMagnifyingGlass,
  user: PhUser,
  star: PhStar,
  money: PhCurrencyDollar,
  gear: PhGear,
  robot: PhRobot,
  clock: PhClock,
  flame: PhFlame,
  trendUp: PhTrendUp,
  trendDown: PhTrendDown,
  arrowUp: PhArrowUp,
  arrowRight: PhArrowRight,
  caretDown: PhCaretDown,
  caretRight: PhCaretRight,
  caretLeft: PhCaretLeft,

  // Legacy names remapped to Phosphor
  'check-circle': PhCheckCircle,
  'alert-triangle': PhWarning,
  'alert-circle': PhWarningCircle,
  lightbulb: PhLightbulb,
  'x-circle': PhXCircle,
  'bar-chart': PhChartBar,
  'trending-up': PhTrendUp,
  'dollar-sign': PhCurrencyDollar,
  'arrow-up': PhArrowUp,
  'arrow-right': PhArrowRight,
  'chevron-down': PhCaretDown,
  'chevron-right': PhCaretRight,
  'chevron-left': PhCaretLeft,
  'shield-check': PhShieldCheck,
  'shield-alert': PhShieldWarning,
  'arrow-up-right': PhArrowUp,
  'minus-circle': PhMinusCircle,
  'plus-circle': PhPlusCircle,
  'message-circle': PhEnvelope,
  'star-filled': PhStar,
  'file-text': PhFileText,
  'edit-3': PhPencil,
  'share-2': PhShare,
  'external-link': PhArrowSquareOut,
  'align-left': PhTextAlignLeft,
  grid: PhGridFour,
  list: PhList,
  smartphone: PhDeviceMobile,
  printer: PhPrinter,
  phone: PhPhone,
  camera: PhCamera,
  folder: PhFolderSimple,
  'help-circle': PhQuestion,
  palette: PhPalette,
  monitor: PhMonitor,
  settings: PhGear,
  sitemap: PhTreeStructure,
  'git-merge': PhGitMerge,
  swords: PhSword,
  'chevrons-right': PhCaretRight,
  check: PhCheck,
  x: PhX,
  plus: PhPlus,
  sparkles: PhSparkle,
  zap: PhLightning,
  rocket: PhRocket,
  briefcase: PhBriefcase,
  menu: PhList,
  bell: PhBell,
  mail: PhEnvelope,
  lock: PhLock,
  key: PhKey,
  eye: PhEye,
  'eye-off': PhEyeSlash,
  copy: PhCopy,
  sliders: PhSliders,
  refresh: PhArrowsClockwise,
  download: PhDownload,
  history: PhClockCounterClockwise,
  external: PhArrowSquareOut,
  bookmark: PhBookmark,
  book: PhBookOpen,
  layers: PhStack,
  activity: PhPulse,
  video: PhVideo,
  play: PhPlay,
  hourglass: PhHourglass,
  award: PhTrophy,
  users: PhUsers,
  logout: PhSignOut,
  login: PhSignIn,
  facebook: PhFacebookLogo,
  linkedin: PhLinkedinLogo,
  twitter: PhTwitterLogo,
  database: PhDatabase,
  calendar: PhCalendar,
  code: PhCode,
  image: PhImage,
  tag: PhTag,
  home: PhHouse,
  cpu: PhCpu,
  map: PhMapPin,
  smile: PhSmiley,
  meh: PhSmileyMeh,
  frown: PhSmileySad,
  youtube: PhYoutubeLogo,
  instagram: PhInstagramLogo,
  info: PhInfo,
  edit: PhPencil,
  user2: PhUsers,
};

// Effort-level icons (used by RevenueForecast)
const EFFORT_ICONS = {
  '30min': PhSparkle,
  '1hr': PhWrench,
  '2hr': PhClock,
  '4hr': PhHourglass,
  '8hr': PhHammer,
};

const phosphorComponent = computed(() => {
  // First check effort-level icons
  if (EFFORT_ICONS[props.name]) return EFFORT_ICONS[props.name];
  // Then semantic/report icons
  if (PHOSPHOR_ICONS[props.name]) return PHOSPHOR_ICONS[props.name];
  // Fall back to legacy inline SVGs (below)
  return null;
});

const phosphorColor = computed(() => {
  if (props.color) return props.color;
  return 'currentColor';
});

const pixelSize = computed(() => {
  const s = props.size;
  if (typeof s === 'number') return s;
  const n = Number(s);
  if (!Number.isNaN(n)) return n;
  return 18;
});

// ── Legacy inline SVG icons (fallback for names not in Phosphor map) ──
const ICONS = {
  // … kept intact for backward compat — only used when phosphorComponent is null
  search:       () => h('g', null, [h('circle', { cx: 11, cy: 11, r: 7 }), h('path', { d: 'm20 20-3.5-3.5' })]),
  briefcase:    () => h('g', null, [h('rect', { width: 20, height: 14, x: 2, y: 5, rx: 2 }), h('path', { d: 'M8 5V3a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' })]),
  edit:         () => h('g', null, [h('path', { d: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' }), h('path', { d: 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' })]),
  sparkles:     () => h('g', null, [h('path', { d: 'M12 3v3M12 18v3M3 12h3M18 12h3M5.5 5.5l2 2M16.5 16.5l2 2M18.5 5.5l-2 2M7.5 16.5l-2 2' })]),
  zap:          () => h('path', { d: 'M13 2 3 14h7l-1 8 10-12h-7l1-8z' }),
  rocket:       () => h('g', null, [h('path', { d: 'M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z' }), h('path', { d: 'm12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z' }), h('path', { d: 'M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0' }), h('path', { d: 'M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5' })]),
  shield:       () => h('path', { d: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' }),
  check:        () => h('path', { d: 'M20 6 9 17l-5-5' }),
  'check-circle': () => h('g', null, [h('circle', { cx: 12, cy: 12, r: 10 }), h('path', { d: 'm8 12 3 3 5-6' })]),
  'arrow-right': () => h('g', null, [h('path', { d: 'M5 12h14' }), h('path', { d: 'm12 5 7 7-7 7' })]),
  'arrow-up-right': () => h('g', null, [h('path', { d: 'M7 7h10v10' }), h('path', { d: 'M7 17 17 7' })]),
  'chevron-down': () => h('path', { d: 'm6 9 6 6 6-6' }),
  'chevron-right': () => h('path', { d: 'm9 6 6 6-6 6' }),
  menu:         () => h('g', null, [h('line', { x1: 4, x2: 20, y1: 6, y2: 6 }), h('line', { x1: 4, x2: 20, y1: 12, y2: 12 }), h('line', { x1: 4, x2: 20, y1: 18, y2: 18 })]),
  x:            () => h('g', null, [h('path', { d: 'M18 6 6 18' }), h('path', { d: 'm6 6 12 12' })]),
  user:         () => h('g', null, [h('path', { d: 'M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2' }), h('circle', { cx: 12, cy: 7, r: 4 })]),
  bell:         () => h('g', null, [h('path', { d: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9' }), h('path', { d: 'M10.3 21a1.94 1.94 0 0 0 3.4 0' })]),
  settings:     () => h('g', null, [h('circle', { cx: 12, cy: 12, r: 3 }), h('path', { d: 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z' })]),
  'bar-chart':  () => h('g', null, [h('line', { x1: 12, x2: 12, y1: 20, y2: 10 }), h('line', { x1: 18, x2: 18, y1: 20, y2: 4 }), h('line', { x1: 6, x2: 6, y1: 20, y2: 14 })]),
  globe:        () => h('g', null, [h('circle', { cx: 12, cy: 12, r: 10 }), h('path', { d: 'M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z' }), h('path', { d: 'M2 12h20' })]),
  link:         () => h('g', null, [h('path', { d: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71' }), h('path', { d: 'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71' })]),
  clock:        () => h('g', null, [h('circle', { cx: 12, cy: 12, r: 10 }), h('path', { d: 'M12 6v6l4 2' })]),
  'trending-up': () => h('g', null, [h('path', { d: 'm22 7-8.5 8.5-5-5L2 17' }), h('path', { d: 'M16 7h6v6' })]),
  info:         () => h('g', null, [h('circle', { cx: 12, cy: 12, r: 10 }), h('path', { d: 'M12 16v-4' }), h('path', { d: 'M12 8h.01' })]),
  'message-circle': () => h('path', { d: 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z' }),
  logout:       () => h('g', null, [h('path', { d: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' }), h('polyline', { points: '16 17 21 12 16 7' }), h('line', { x1: 21, x2: 9, y1: 12, y2: 12 })]),
  login:        () => h('g', null, [h('path', { d: 'M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4' }), h('polyline', { points: '10 17 15 12 10 7' }), h('line', { x1: 15, x2: 3, y1: 12, y2: 12 })]),
  mail:         () => h('g', null, [h('rect', { width: 20, height: 16, x: 2, y: 4, rx: 2 }), h('path', { d: 'm22 7-10 5L2 7' })]),
  lock:         () => h('g', null, [h('rect', { width: 18, height: 11, x: 3, y: 11, rx: 2 }), h('path', { d: 'M7 11V7a5 5 0 0 1 10 0v4' })]),
  eye:          () => h('g', null, [h('path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' }), h('circle', { cx: 12, cy: 12, r: 3 })]),
  'eye-off':    () => h('g', null, [h('path', { d: 'M9.88 9.88a3 3 0 1 0 4.24 4.24' }), h('path', { d: 'M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68' }), h('path', { d: 'M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61' }), h('line', { x1: 2, x2: 22, y1: 2, y2: 22 })]),
  copy:         () => h('g', null, [h('rect', { width: 14, height: 14, x: 8, y: 8, rx: 2 }), h('path', { d: 'M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2' })]),
  sliders:      () => h('g', null, [h('line', { x1: 4, x2: 4, y1: 21, y2: 14 }), h('line', { x1: 4, x2: 4, y1: 10, y2: 3 }), h('line', { x1: 12, x2: 12, y1: 21, y2: 12 }), h('line', { x1: 12, x2: 12, y1: 8, y2: 3 }), h('line', { x1: 20, x2: 20, y1: 21, y2: 16 }), h('line', { x1: 20, x2: 20, y1: 12, y2: 3 }), h('line', { x1: 2, x2: 6, y1: 14, y2: 14 }), h('line', { x1: 10, x2: 14, y1: 8, y2: 8 }), h('line', { x1: 18, x2: 22, y1: 16, y2: 16 })]),
  'alert-triangle': () => h('g', null, [h('path', { d: 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' }), h('line', { x1: 12, x2: 12, y1: 9, y2: 13 }), h('line', { x1: 12, x2: 12.01, y1: 17, y2: 17 })]),
  refresh:      () => h('g', null, [h('path', { d: 'M21 12a9 9 0 1 1-3-6.7L21 8' }), h('path', { d: 'M21 3v5h-5' })]),
  download:     () => h('g', null, [h('path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' }), h('polyline', { points: '7 10 12 15 17 10' }), h('line', { x1: 12, x2: 12, y1: 15, y2: 3 })]),
  history:      () => h('g', null, [h('path', { d: 'M3 3v5h5' }), h('path', { d: 'M3.05 13A9 9 0 1 0 6 5.3L3 8' }), h('path', { d: 'M12 7v5l4 2' })]),
  plus:         () => h('g', null, [h('path', { d: 'M12 5v14' }), h('path', { d: 'M5 12h14' })]),
  external:     () => h('g', null, [h('path', { d: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' }), h('polyline', { points: '15 3 21 3 21 9' }), h('line', { x1: 10, x2: 21, y1: 14, y2: 3 })]),
  star:         () => h('polygon', { points: '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2' }),
  bookmark:     () => h('path', { d: 'M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z' }),
  target:       () => h('g', null, [h('circle', { cx: 12, cy: 12, r: 10 }), h('circle', { cx: 12, cy: 12, r: 6 }), h('circle', { cx: 12, cy: 12, r: 2 })]),
  book:         () => h('g', null, [h('path', { d: 'M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20' })]),
  layers:       () => h('g', null, [h('polygon', { points: '12 2 2 7 12 12 22 7 12 2' }), h('polyline', { points: '2 17 12 22 22 17' }), h('polyline', { points: '2 12 12 17 22 12' })]),
  'minus-circle':   () => h('g', null, [h('circle', { cx: 12, cy: 12, r: 10 }), h('path', { d: 'M8 12h8' })]),
  'alert-circle':   () => h('g', null, [h('circle', { cx: 12, cy: 12, r: 10 }), h('line', { x1: 12, y1: 8, x2: 12, y2: 12 }), h('line', { x1: 12, y1: 16, x2: 12.01, y2: 16 })]),
  lightbulb:        () => h('g', null, [h('path', { d: 'M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5' }), h('path', { d: 'M9 18h6' }), h('path', { d: 'M10 22h4' })]),
  activity:         () => h('polyline', { points: '22 12 18 12 15 21 9 3 6 12 2 12' }),
  'star-filled':    () => h('polygon', { points: '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2', fill: 'currentColor', stroke: 'none' }),
  'chevron-left': () => h('path', { d: 'm15 18-6-6 6-6' }),
  'share-2':      () => h('g', null, [
    h('circle', { cx: 18, cy: 5, r: 3 }),
    h('circle', { cx: 6, cy: 12, r: 3 }),
    h('circle', { cx: 18, cy: 19, r: 3 }),
    h('line', { x1: 8.59, y1: 13.51, x2: 15.42, y2: 17.49 }),
    h('line', { x1: 15.41, y1: 6.51, x2: 8.59, y2: 10.49 }),
  ]),
  'external-link': () => h('g', null, [
    h('path', { d: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' }),
    h('polyline', { points: '15 3 21 3 21 9' }),
    h('line', { x1: 10, y1: 14, x2: 21, y2: 3 }),
  ]),
  'align-left':   () => h('g', null, [
    h('line', { x1: 21, y1: 6, x2: 3, y2: 6 }),
    h('line', { x1: 15, y1: 12, x2: 3, y2: 12 }),
    h('line', { x1: 17, y1: 18, x2: 3, y2: 18 }),
  ]),
  'video':        () => h('g', null, [
    h('rect', { x: 2, y: 3, width: 20, height: 14, rx: 2 }),
    h('polygon', { points: '9 7 15 10 9 13 9 7' }),
  ]),
  'play':         () => h('polygon', { points: '5 3 19 12 5 21 5 3' }),
  'dollar-sign': () => h('g', null, [
    h('line', { x1: 12, y1: 2, x2: 12, y2: 22 }),
    h('path', { d: 'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' }),
  ]),
  grid:         () => h('g', null, [
    h('rect', { x: 3, y: 3, width: 7, height: 7, rx: 1 }),
    h('rect', { x: 14, y: 3, width: 7, height: 7, rx: 1 }),
    h('rect', { x: 14, y: 14, width: 7, height: 7, rx: 1 }),
    h('rect', { x: 3, y: 14, width: 7, height: 7, rx: 1 }),
  ]),
  list:         () => h('g', null, [
    h('line', { x1: 8, y1: 6, x2: 21, y2: 6 }),
    h('line', { x1: 8, y1: 12, x2: 21, y2: 12 }),
    h('line', { x1: 8, y1: 18, x2: 21, y2: 18 }),
    h('line', { x1: 3, y1: 6, x2: 3.01, y2: 6 }),
    h('line', { x1: 3, y1: 12, x2: 3.01, y2: 12 }),
    h('line', { x1: 3, y1: 18, x2: 3.01, y2: 18 }),
  ]),
  gear:         () => h('g', null, [h('circle', { cx: 12, cy: 12, r: 3 }), h('path', { d: 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-3 3l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-3-3l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 3-3l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 3 3l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z' })]),
  flame:        () => h('path', { d: 'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z' }),
  'x-circle':   () => h('g', null, [h('circle', { cx: 12, cy: 12, r: 10 }), h('path', { d: 'm15 9-6 6' }), h('path', { d: 'm9 9 6 6' })]),
  users:        () => h('g', null, [h('path', { d: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' }), h('circle', { cx: 9, cy: 7, r: 4 }), h('path', { d: 'M22 21v-2a4 4 0 0 0-3-3.87' }), h('path', { d: 'M16 3.13a4 4 0 0 1 0 7.75' })]),
  palette:      () => h('g', null, [h('circle', { cx: 12, cy: 12, r: 10 }), h('path', { d: 'M12 2a10 10 0 1 0 5.77 18.45' }), h('path', { d: 'M12 2v20' }), h('path', { d: 'M2 12h20' }), h('circle', { cx: 12, cy: 12, r: 2, fill: 'currentColor', stroke: 'none' })]),
  'plus-circle': () => h('g', null, [h('circle', { cx: 12, cy: 12, r: 10 }), h('path', { d: 'M12 8v8' }), h('path', { d: 'M8 12h8' })]),
  hourglass:    () => h('g', null, [h('path', { d: 'M5 22h14' }), h('path', { d: 'M5 2h14' }), h('path', { d: 'M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22' }), h('path', { d: 'M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2' })]),
  award:        () => h('g', null, [h('circle', { cx: 12, cy: 8, r: 6 }), h('path', { d: 'M8.21 13.89 7 23l5-3 5 3-1.21-9.11' })]),
  'arrow-up':   () => h('g', null, [h('path', { d: 'M12 19V5' }), h('path', { d: 'm5 12 7-7 7 7' })]),
  facebook:     () => h('path', { d: 'M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z' }),
  linkedin:     () => h('g', null, [h('rect', { x: 2, y: 2, width: 20, height: 20, rx: 2 }), h('path', { d: 'M8 11v6M8 8v.01M12 16v-5M16 16v-3a2 2 0 0 0-4 0' })]),
  twitter:      () => h('path', { d: 'M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z' }),
  key:          () => h('g', null, [h('path', { d: 'M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z' }), h('circle', { cx: 16.5, cy: 7.5, r: .5, fill: 'currentColor' })]),
  database:     () => h('g', null, [h('ellipse', { cx: 12, cy: 5, rx: 9, ry: 3 }), h('path', { d: 'M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5' }), h('path', { d: 'M3 12c0 1.66 4 3 9 3s9-1.34 9-3' })]),
  smartphone:   () => h('g', null, [h('rect', { x: 5, y: 2, width: 14, height: 20, rx: 2 }), h('path', { d: 'M12 18h.01' })]),
  monitor:      () => h('g', null, [h('rect', { x: 2, y: 3, width: 20, height: 14, rx: 2 }), h('line', { x1: 8, x2: 16, y1: 21, y2: 21 }), h('line', { x1: 12, x2: 12, y1: 17, y2: 21 })]),
  calendar:     () => h('g', null, [h('rect', { x: 3, y: 4, width: 18, height: 18, rx: 2 }), h('line', { x1: 16, x2: 16, y1: 2, y2: 6 }), h('line', { x1: 8, x2: 8, y1: 2, y2: 6 }), h('line', { x1: 3, x2: 21, y1: 10, y2: 10 })]),
  code:         () => h('g', null, [h('polyline', { points: '16 18 22 12 16 6' }), h('polyline', { points: '8 6 2 12 8 18' })]),
  image:        () => h('g', null, [h('rect', { x: 2, y: 2, width: 20, height: 20, rx: 2 }), h('circle', { cx: 8.5, cy: 8.5, r: 2.5 }), h('path', { d: 'M21 15l-5-4-3 3-4-4-5 5' })]),
  'file-text':  () => h('g', null, [h('path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }), h('polyline', { points: '14 2 14 8 20 8' }), h('line', { x1: 16, x2: 8, y1: 13, y2: 13 }), h('line', { x1: 16, x2: 8, y1: 17, y2: 17 }), h('polyline', { points: '10 9 9 9 8 9' })]),
  sitemap:      () => h('g', null, [h('path', { d: 'M12 3v4' }), h('path', { d: 'M8 7h8' }), h('rect', { x: 4, y: 7, width: 16, height: 4, rx: 1 }), h('path', { d: 'M12 11v4' }), h('rect', { x: 8, y: 15, width: 8, height: 4, rx: 1 })]),
  tag:          () => h('g', null, [h('path', { d: 'M12 2H2v10l9.17 9.17a2 2 0 0 0 2.83 0l7-7a2 2 0 0 0 0-2.83L12 2z' }), h('circle', { cx: 7, cy: 7, r: 1.5, fill: 'currentColor', stroke: 'none' })]),
  robot:        () => h('g', null, [h('rect', { x: 3, y: 11, width: 18, height: 10, rx: 2 }), h('circle', { cx: 9, cy: 15, r: 1.5, fill: 'currentColor', stroke: 'none' }), h('circle', { cx: 15, cy: 15, r: 1.5, fill: 'currentColor', stroke: 'none' }), h('path', { d: 'M8 9V7a4 4 0 1 1 8 0v2' })]),
  smile: () => h('g', null, [
    h('circle', { cx: 12, cy: 12, r: 10 }),
    h('path', { d: 'M8 14s1.5 2 4 2 4-2 4-2' }),
    h('line', { x1: 9, y1: 9, x2: 9.01, y2: 9 }),
    h('line', { x1: 15, y1: 9, x2: 15.01, y2: 9 }),
  ]),
  meh: () => h('g', null, [
    h('circle', { cx: 12, cy: 12, r: 10 }),
    h('line', { x1: 8, y1: 14, x2: 16, y2: 14 }),
    h('line', { x1: 9, y1: 9, x2: 9.01, y2: 9 }),
    h('line', { x1: 15, y1: 9, x2: 15.01, y2: 9 }),
  ]),
  frown: () => h('g', null, [
    h('circle', { cx: 12, cy: 12, r: 10 }),
    h('path', { d: 'M16 16s-1.5-2-4-2-4 2-4 2' }),
    h('line', { x1: 9, y1: 9, x2: 9.01, y2: 9 }),
    h('line', { x1: 15, y1: 9, x2: 15.01, y2: 9 }),
  ]),
  youtube: () => h('g', null, [
    h('path', { d: 'M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.56 49.56 0 0 1-16.2 0A2 2 0 0 1 2.5 17' }),
    h('polygon', { points: '10 9 15 12 10 15 10 9' }),
  ]),
  instagram: () => h('g', null, [
    h('rect', { x: 2, y: 2, width: 20, height: 20, rx: 5 }),
    h('circle', { cx: 12, cy: 12, r: 5 }),
    h('line', { x1: 17.5, y1: 6.5, x2: 17.51, y2: 6.5 }),
  ]),
  home: () => h('g', null, [
    h('path', { d: 'm3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z' }),
    h('polyline', { points: '9 22 9 12 15 12 15 22' }),
  ]),
  'git-merge': () => h('g', null, [
    h('circle', { cx: 18, cy: 18, r: 3 }),
    h('circle', { cx: 6, cy: 6, r: 3 }),
    h('path', { d: 'M6 21V9a9 9 0 0 0 9 9' }),
  ]),
  cpu: () => h('g', null, [
    h('rect', { x: 4, y: 4, width: 16, height: 16, rx: 2 }),
    h('rect', { x: 9, y: 9, width: 6, height: 6 }),
    h('line', { x1: 9, y1: 1, x2: 9, y2: 4 }),
    h('line', { x1: 15, y1: 1, x2: 15, y2: 4 }),
    h('line', { x1: 9, y1: 20, x2: 9, y2: 23 }),
    h('line', { x1: 15, y1: 20, x2: 15, y2: 23 }),
    h('line', { x1: 20, y1: 9, x2: 23, y2: 9 }),
    h('line', { x1: 20, y1: 14, x2: 23, y2: 14 }),
    h('line', { x1: 1, y1: 9, x2: 4, y2: 9 }),
    h('line', { x1: 1, y1: 14, x2: 4, y2: 14 }),
  ]),
  map: () => h('g', null, [
    h('polygon', { points: '3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21' }),
    h('line', { x1: 9, y1: 3, x2: 9, y2: 18 }),
    h('line', { x1: 15, y1: 6, x2: 15, y2: 21 }),
  ]),
  swords: () => h('g', null, [
    h('line', { x1: 5, y1: 3, x2: 19, y2: 17 }),
    h('line', { x1: 19, y1: 3, x2: 5, y2: 17 }),
    h('line', { x1: 5, y1: 21, x2: 8, y2: 18 }),
    h('line', { x1: 19, y1: 21, x2: 16, y2: 18 }),
  ]),
  'chevrons-right': () => h('g', null, [
    h('path', { d: 'm6 17 5-5-5-5' }),
    h('path', { d: 'm13 17 5-5-5-5' }),
  ]),
  'edit-3': () => h('g', null, [
    h('path', { d: 'M12 20h9' }),
    h('path', { d: 'M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z' }),
  ]),
  'shield-check': () => h('g', null, [
    h('path', { d: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' }),
    h('path', { d: 'm9 12 2 2 4-4' }),
  ]),
  'shield-alert': () => h('g', null, [
    h('path', { d: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' }),
    h('line', { x1: 12, y1: 8, x2: 12, y2: 12 }),
    h('line', { x1: 12, y1: 16, x2: 12.01, y2: 16 }),
  ]),
};

const path = computed(() => {
  const render = ICONS[props.name];
  if (!render) {
    console.warn(`Icon "${props.name}" not found, using fallback`);
    return () => h('circle', { cx: 12, cy: 12, r: 1 });
  }
  return render;
});
</script>
